import { flexRender, type Table } from "@tanstack/react-table";
import { TableErrorState } from "#/components/ui/table-error-state";
import { cn } from "#/lib/utils";

type DataTableProps<TData> = {
	table: Table<TData>;
	emptyMessage: string;
	isLoading?: boolean;
	loadingMessage?: string;
	heightClassName?: string;
	contentClassName?: string;
	isError?: boolean;
	errorMessage?: string;
	onRetry?: () => void;
};

export function DataTable<TData>({
	table,
	emptyMessage,
	isLoading = false,
	loadingMessage = "Loading...",
	heightClassName,
	contentClassName,
	isError = false,
	errorMessage,
	onRetry,
}: DataTableProps<TData>) {
	const rows = table.getRowModel().rows;
	const message = isLoading ? loadingMessage : emptyMessage;
	const bodyClassName = cn(
		"overflow-y-auto overflow-x-hidden",
		heightClassName,
	);

	return (
		<div className="overflow-x-auto scrollbar-none">
			<div className={cn("min-w-full", contentClassName)}>
				<table className="w-full table-fixed text-left text-label2">
					<thead>
						{table.getHeaderGroups().map((hg) => (
							<tr
								key={hg.id}
								className="border-b border-border text-muted-foreground"
							>
								{hg.headers.map((h) => (
									<th
										key={h.id}
										className="pb-2 pr-4 text-label2-medium whitespace-nowrap"
									>
										{flexRender(h.column.columnDef.header, h.getContext())}
									</th>
								))}
							</tr>
						))}
					</thead>
				</table>
				<div className={bodyClassName} style={{ scrollbarGutter: "stable" }}>
					<table className="w-full table-fixed text-left text-label2">
						<tbody>
							{rows.length === 0 ? (
								<tr>
									<td
										colSpan={table.getAllColumns().length}
										className="py-4 text-center text-muted-foreground"
									>
										{isError ? (
											<TableErrorState
												message={errorMessage}
												onRetry={onRetry}
											/>
										) : (
											<span className="block py-4">{message}</span>
										)}
									</td>
								</tr>
							) : (
								rows.map((row) => (
									<tr
										key={row.id}
										className="border-b border-border/50 text-foreground"
									>
										{row.getVisibleCells().map((cell) => (
											<td
												key={cell.id}
												className="py-2.5 pr-4 whitespace-nowrap"
											>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</td>
										))}
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
}
